import { eq, useLiveQuery } from "@tanstack/react-db";
import { useCallback, useMemo } from "react";
import { useTranslations } from "use-intl";
import { PersonAvatar } from "@/components/person-avatar";
import { SlidePanel } from "@/components/ui/slide-panel";
import { membershipCollection } from "@/lib/collections/membership";
import type { Staff } from "@/lib/collections/staff";
import { useApp } from "@/lib/context/use-powersync";
import { useAllStaff } from "@/lib/hooks/use-all-staff";
import { getInitialsFromDisplayName, getStaffDisplayName } from "@/lib/utils";

export interface TransferPickerProps {
  /** Called when the panel should close without transferring. */
  onClose: () => void;
  /** Called with the chosen transferee's membership id. */
  onSelect: (memberId: string) => void;
}

/**
 * One selectable staff row. Extracted to module scope so its click handler
 * gets a stable identity via its own useCallback, instead of a fresh closure
 * on every parent render's `.map()` pass.
 */
function TransferTargetRow({
  memberId,
  staff,
  onSelect,
}: {
  memberId: string;
  onSelect: (memberId: string) => void;
  staff: Staff;
}) {
  const name = getStaffDisplayName(staff);
  const handleClick = useCallback(() => {
    onSelect(memberId);
  }, [memberId, onSelect]);

  return (
    <li>
      <button
        className="flex w-full items-center gap-3 p-4 text-left hover:bg-muted/50"
        onClick={handleClick}
        type="button"
      >
        <PersonAvatar
          alt={name}
          avatar={staff.image ?? undefined}
          initials={getInitialsFromDisplayName(name)}
          size="md"
        />
        <span className="font-medium text-foreground text-sm">{name}</span>
      </button>
    </li>
  );
}

/**
 * Staff picker for transferring the active call. Lists every staff member in
 * the current property except the logged-in user, resolving each membership
 * (the `memberId` the transfer API expects) to its staff profile for display.
 *
 * Mounted only while the picker is open, so the live queries here never run
 * in screens (or tests) that don't open it.
 */
export function TransferPicker({ onClose, onSelect }: TransferPickerProps) {
  const t = useTranslations("communications.transferPicker");
  const { currentProperty, currentStaff } = useApp();
  const propertyId = currentProperty?.id ?? null;

  const { data: memberships = [] } = useLiveQuery(
    (q) =>
      q
        .from({ m: membershipCollection })
        .where(({ m }) => eq(m.propertyId, propertyId ?? "")),
    [propertyId]
  );
  const { staff: staffList } = useAllStaff();

  const targets = useMemo(() => {
    type StaffRow = (typeof staffList)[number];
    const out: { memberId: string; staff: StaffRow }[] = [];
    for (const m of memberships) {
      if (m.userId === currentStaff?.id) {
        continue;
      }
      const staff = staffList.find((s) => s.id === m.userId);
      if (staff) {
        out.push({ memberId: m.id, staff });
      }
    }
    return out;
  }, [memberships, staffList, currentStaff?.id]);

  return (
    <SlidePanel onClose={onClose} title={t("title")}>
      {targets.length === 0 ? (
        <p className="p-6 text-muted-foreground text-sm">{t("empty")}</p>
      ) : (
        <ul className="divide-y divide-border">
          {targets.map(({ memberId, staff }) => (
            <TransferTargetRow
              key={memberId}
              memberId={memberId}
              onSelect={onSelect}
              staff={staff}
            />
          ))}
        </ul>
      )}
    </SlidePanel>
  );
}
